// The developer's home: activity, money and what needs attention across their
// games, for live or sandbox, from GET /v1/developer/overview.
import { ActivityBars } from "../../components/ActivityBars";
import { AuditTable } from "../../components/AuditTable";
import { Shell } from "../../components/Shell";
import { Empty, ErrorNotice, GameStatusBadge, Loading, Money, PageHead, Panel, Segmented, Select } from "../../components/ui";
import { developerApi } from "../../lib/api";
import { formatCount } from "../../lib/money";
import { useQuery } from "../../lib/query";
import { Link, navigate, useSearchParam } from "../../lib/router";
import { useClient, useSession } from "../../lib/session";
import { relativeTime } from "../../lib/time";
import type { AuditLog, DeveloperOverview, Env } from "../../lib/types";
import { DECLINE_LABELS, TXN_LABELS, attention, defaultEnv, label, weekChange } from "./insights";

function Figure({ label: l, value, note, tone }: { label: string; value: string; note?: string; tone?: "up" | "down" | "flat" }) {
  return (
    <div className="figure">
      <div className="label">{l}</div>
      <div className="value">{value}</div>
      {note && <div className={`split${tone ? ` trend-${tone}` : ""}`}>{note}</div>}
    </div>
  );
}

const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : "–");

export function DevOverview() {
  const api = developerApi(useClient());
  const { session } = useSession();
  const [envParam, setEnv] = useSearchParam("env");
  const [game, setGame] = useSearchParam("game");
  const games = useQuery("dev:games", () => api.games());
  const ov = useQuery(`dev:overview:${game}`, () => api.overview(game || undefined), { refreshMs: 60_000 });
  const o = ov.data;
  const env: Env = envParam === "production" || envParam === "sandbox" ? envParam : o ? defaultEnv(o) : "production";
  const noGames = games.data && games.data.data.length === 0;

  return (
    <Shell crumbs={[{ label: "Overview" }]}>
      <PageHead
        title={`Welcome back, ${session?.name}`}
        lede={o ? `Updated ${relativeTime(o.generated_at)}. Live figures come from production keys; sandbox ones from testing.` : undefined}
        actions={!noGames && (
          <div className="row" style={{ gap: 10 }}>
            <Select aria-label="Game" value={game} onChange={(e) => setGame(e.target.value)} style={{ width: 200 }}>
              <option value="">All games</option>
              {(games.data?.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
            <Segmented label="Environment" value={env} onChange={(v) => setEnv(v)}
              options={[{ value: "production", label: "Live" }, { value: "sandbox", label: "Sandbox" }]} />
          </div>
        )}
      />
      <ErrorNotice error={ov.error ?? games.error} onRetry={ov.reload} />
      {noGames ? (
        <Panel>
          <Empty title="Start with your first game" action={<Link className="btn primary" to="/dev/games">Create a game</Link>}>
            Each game gets its own banks, keys and clock, with a sandbox to try everything. Your figures appear here once money starts moving.
          </Empty>
        </Panel>
      ) : !o ? (
        ov.loading && <Loading />
      ) : (
        <Body o={o} env={env} oneGame={!!game} />
      )}
    </Shell>
  );
}

function Body({ o, env, oneGame }: { o: DeveloperOverview; env: Env; oneGame: boolean }) {
  const week = o.transactions_7d[env];
  const change = weekChange(week, o.transactions_prev_7d[env]);
  const items = attention(o, env);
  const money = o.money.filter((m) => m.environment === env);
  const mix = o.transaction_mix_7d.map((m) => ({ type: m.type, n: m[env] })).filter((m) => m.n > 0);
  const mixMax = Math.max(1, ...mix.map((m) => m.n));
  const wh = o.webhooks;
  const live = env === "production";

  return (
    <div className="stack">
      <div className="figure-row">
        <Figure label="Transactions, last 7 days" value={formatCount(week)} note={change.text} tone={change.tone} />
        <Figure label="Transactions, last 24 h" value={formatCount(o.transactions_24h[env])} />
        <Figure label="Player accounts" value={formatCount(o.player_accounts[env])}
          note={o.new_accounts_7d[env] ? `+${formatCount(o.new_accounts_7d[env])} this week` : "None new this week"} />
        <Figure label="Players" value={formatCount(o.players[env])} />
        <Figure label="Active cards" value={formatCount(o.active_cards[env])}
          note={`${formatCount(o.active_holds[env])} held payments, ${formatCount(o.active_fixed_deposits[env])} fixed deposits`} />
      </div>

      <div className="grid-2">
        <Panel title="Transactions per day" sub="Last 14 days, live and sandbox together.">
          <ActivityBars days={o.activity_14d} />
        </Panel>
        <Panel title="Needs attention" sub={items.length ? `${items.length} ${items.length === 1 ? "thing" : "things"} to look at in ${live ? "live" : "sandbox"}.` : undefined} flush>
          {items.length === 0 ? (
            <div className="all-clear">
              <span className="all-clear-dot" aria-hidden="true">✓</span>
              <div>
                <strong>All clear</strong>
                <p className="muted small">Webhooks are delivering, keys are in use and card payments look healthy.</p>
              </div>
            </div>
          ) : (
            <ul className="attention">
              {items.map((i) => (
                <li key={i.id} data-severity={i.severity}>
                  <span className="attention-dot" aria-hidden="true" />
                  <div className="grow">
                    <div className="cell-title">{i.title}</div>
                    <p className="muted small">{i.body}</p>
                  </div>
                  {i.to && <Link className="btn small" to={i.to}>{i.cta ?? "Open"}</Link>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel title="Money by bank" sub={`${live ? "Live" : "Sandbox"} banks. In circulation is everything funded, less what was reversed.`} flush>
        {money.length === 0 ? (
          <p className="muted panel-body">
            No {live ? "live" : "sandbox"} banks yet.{" "}
            {o.per_game[0] && <Link to={`/dev/games/${o.per_game[0].id}/banks${live ? "?env=production" : ""}`}>Create one</Link>}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>Bank</th><th className="num">In circulation</th><th className="num">Moved, 7 days</th><th className="num">Fees earned</th><th className="num">In savings</th></tr>
              </thead>
              <tbody>
                {money.map((m) => (
                  <tr key={m.bank_id}>
                    <td>
                      <Link className="cell-title" to={`/dev/games/${m.game_id}/banks${live ? "?env=production" : ""}`}>{m.bank_name}</Link>
                      <div className="cell-sub">{m.game_name} · code {m.bank_code} · {m.currency.code}</div>
                    </td>
                    <td className="num"><Money minor={m.issued_minor} currency={m.currency} /></td>
                    <td className="num"><Money minor={m.moved_minor_7d} currency={m.currency} /></td>
                    <td className="num"><Money minor={m.fee_income_minor} currency={m.currency} /></td>
                    <td className="num">
                      <Money minor={m.savings_minor + m.fixed_deposits_minor} currency={m.currency} />
                      {m.fixed_deposits_minor > 0 && <div className="cell-sub">incl. <Money minor={m.fixed_deposits_minor} currency={m.currency} /> fixed</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="grid-3">
        <Panel title="What moved this week" sub={`${live ? "Live" : "Sandbox"} transactions by type.`}>
          {mix.length === 0 ? (
            <p className="muted small">Nothing yet this week.</p>
          ) : (
            <ul className="mix">
              {mix.map((m) => (
                <li key={m.type}>
                  <span>{label(TXN_LABELS, m.type)}</span>
                  <span className="mix-bar"><i style={{ width: `${(m.n / mixMax) * 100}%` }} /></span>
                  <span className="num">{formatCount(m.n)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Card payments" sub="Last 7 days, live and sandbox.">
          <dl className="facts">
            <dt>Payments</dt>
            <dd>{formatCount(o.cards.authorizations_7d)}</dd>
            <dt>Declined</dt>
            <dd>{formatCount(o.cards.declined_7d)} ({pct(o.cards.declined_7d, o.cards.authorizations_7d)})</dd>
          </dl>
          {o.cards.decline_codes_7d.length > 0 && (
            <ul className="mix compact">
              {o.cards.decline_codes_7d.map((c) => (
                <li key={c.code}><span>{label(DECLINE_LABELS, c.code)}</span><span className="num">{formatCount(c.count)}</span></li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Webhook delivery" sub="Last 24 hours, live and sandbox.">
          <dl className="facts">
            <dt>Endpoints</dt>
            <dd>{o.webhooks.endpoints.production} live, {o.webhooks.endpoints.sandbox} sandbox</dd>
            <dt>Delivered</dt>
            <dd>{formatCount(wh.delivered_24h)} of {formatCount(wh.deliveries_24h)} ({pct(wh.delivered_24h, wh.deliveries_24h)})</dd>
            <dt>Failed</dt>
            <dd>{formatCount(wh.failed_24h)}</dd>
            <dt>Waiting or retrying</dt>
            <dd>{formatCount(wh.pending)}</dd>
          </dl>
        </Panel>
      </div>

      {!oneGame && (
        <Panel title="Your games" actions={<Link to="/dev/games">All games</Link>} flush>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>Game</th><th>Status</th><th className="num">Player accounts</th><th className="num">Transactions, 7 days</th><th>Last activity</th></tr>
              </thead>
              <tbody>
                {o.per_game.map((g) => (
                  <tr key={g.id} className="clickable" onClick={() => navigate(`/dev/games/${g.id}`)}>
                    <td>
                      <Link className="cell-title" to={`/dev/games/${g.id}`} onClick={(e) => e.stopPropagation()}>{g.name}</Link>
                      <div className="cell-sub">{g.live_keys > 0 ? "Live" : "Sandbox only"} · {g.banks.sandbox + g.banks.production} {g.banks.sandbox + g.banks.production === 1 ? "bank" : "banks"}</div>
                    </td>
                    <td><GameStatusBadge status={g.status} /></td>
                    <td className="num">{formatCount(g.player_accounts[env])}</td>
                    <td className="num">{formatCount(g.transactions_7d[env])}</td>
                    <td>{g.last_transaction_at ? relativeTime(g.last_transaction_at) : <span className="muted">No transactions yet</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <Panel title="Recent activity" sub="The latest changes and money movements, live and sandbox." flush>
        <AuditTable rows={o.recent_activity.map((a) => ({ ...a, developer_id: null, correlation_id: null }) as AuditLog)} compact />
      </Panel>
    </div>
  );
}
